/**
 * Overnatting for én act (PERFORMANCE.md, fane 5).
 *
 * ---------------------------------------------------------------------------------------------
 * A TREE IN ONE ANSWER: stays, their rooms, and who is in each.
 *
 * The page draws a card per place with a room table inside it, and every one of those tables needs
 * the occupants. Three endpoints would be three requests to draw one screen, and the room list is
 * bounded — `MAX_STAY_ROOMS` — so reading it whole is a known cost. The same call the travel tab
 * made about passengers, for the same reason.
 *
 * ---------------------------------------------------------------------------------------------
 * OCCUPANTS POINT AT `performance_people`, NOT AT CONTACTS.
 *
 * Only the travelling party gets a room, and «personens rom skal vises tilbake i People-listen»
 * needs the reverse lookup — which a contact id cannot give, because one contact can be on two acts
 * in the same year and would then appear to sleep in both hotels.
 *
 * ---------------------------------------------------------------------------------------------
 * A ROOM'S NIGHTS ARE NULLABLE AND FALL BACK TO THE STAY'S. A late arrival who takes the room from
 * the 16th when everybody else has it from the 15th is ordinary; a room that could only inherit
 * would force a second stay for one person.
 */
import { z } from 'zod';

import { urlInputSchema } from '../url.js';
import { clockTimeSchema, contextAllocationSchema } from './resources.js';

import { forWhomSchema, oneOwner, ONE_OWNER } from './logistics-owner.js';
import { rowViewerSchema } from './row-viewer.js';

export const MAX_STAYS_PER_ACT = 10;
export const MAX_STAY_ROOMS = 60;
export const MAX_STAY_TEXT = 200;

/** One occupant, as the chip a room row draws. */
export const stayOccupantSchema = z
  .object({
    performancePersonId: z.uuid(),
    displayName: z.string(),
    role: z.string().nullable(),
  })
  .meta({ id: 'StayOccupant' });
export type StayOccupant = z.infer<typeof stayOccupantSchema>;

export const stayRoomDtoSchema = z
  .object({
    id: z.uuid(),
    stayId: z.uuid(),
    /** Null: the hotel has not given one out yet (QA 13, 71). Shown as «Rom (nummer ikke satt)». */
    roomNumber: z.string().nullable(),
    roomType: z.string().nullable(),
    /** Null falls back to the stay's window — see the header. */
    nightsFrom: z.string().nullable(),
    nightsTo: z.string().nullable(),
    occupants: z.array(stayOccupantSchema),
    /**
     * RESSURSEN ROMMET ER BOOKET MOT, når det er det (spesifikasjonen §6, trinn 4).
     *
     * Null er det vanlige og det riktige for alt som lå her før: da gjelder `roomNumber`. Er den
     * satt, viser skjermen ressursens navn og skjuler friteksten — de to er to svar på «hvor bor
     * de», og å vise begge er å invitere til at de blir uenige (analysen §3.7).
     *
     * Byrået leser den SAMME raden. Derfor er formen `ContextAllocation` og ikke et objekt med
     * romtypens felt: de skal se «Rom 314, natt til 16.», ikke prisen.
     */
    allocation: contextAllocationSchema.nullable(),
  })
  .meta({ id: 'StayRoomDto' });
export type StayRoomDto = z.infer<typeof stayRoomDtoSchema>;

export const performanceStayDtoSchema = z
  .object({
    id: z.uuid(),
    /** Null for a stay that belongs to the year rather than to an act — sak 78.1. */
    performanceId: z.uuid().nullable(),
    editionId: z.uuid(),
    /** «For hvem», free text. What a room booked for somebody who is not an act is called. */
    forWhom: z.string().nullable(),
    name: z.string(),
    address: z.string().nullable(),
    /** Friteksten — det skjemaet skriver. Det som vises, er `placeContact` når den finnes. */
    contactName: z.string().nullable(),
    contactPhone: z.string().nullable(),
    /**
     * RESSURSEN VINNER OVER FRITEKSTEN (76, grep C2, 0946): den ansvarlige for ressursen et av
     * rommene er booket på. Null uten en slik ressurs, og da gjelder friteksten. Valgfri i
     * utdatatypen, som de andre feltene bølgene la til; API-et sender den alltid.
     */
    placeContact: z
      .object({
        name: z.string().nullable(),
        phone: z.string().nullable(),
        resourceName: z.string(),
      })
      .nullable()
      .optional(),
    bookingReference: z.string().nullable(),
    /** The hotel's own page, `http(s)` only. Null is the ordinary answer — see `stayUrlSchema`. */
    websiteUrl: z.string().nullable(),
    /**
     * Calendar days with no clock and no zone: a hotel night is the 15th wherever you stand, and
     * putting it through a timezone is how it becomes the 14th for half the world.
     */
    checkIn: z.string().nullable(),
    checkOut: z.string().nullable(),
    /**
     * PLANLAGT INN- OG UTSJEKK, «HH:MM» i årgangens sone (QA13 punkt 72). Null: ikke satt, og da
     * står oppholdet ikke i timeplanen. Ingen standard: 15:00 var en gjetning ingen hadde gjort.
     */
    checkInTime: z.string().nullable(),
    checkOutTime: z.string().nullable(),
    notes: z.string().nullable(),
    rooms: z.array(stayRoomDtoSchema),
    /** Hva leseren kan gjøre med raden (D1), regnet av `can()` på serveren. */
    viewer: rowViewerSchema.optional(),
  })
  .meta({ id: 'PerformanceStayDto' });
export type PerformanceStayDto = z.infer<typeof performanceStayDtoSchema>;

export const performanceStayListResponseSchema = z
  .object({ items: z.array(performanceStayDtoSchema) })
  .meta({ id: 'PerformanceStayListResponse' });
export type PerformanceStayListResponse = z.infer<typeof performanceStayListResponseSchema>;

export const listPerformanceStaysQuerySchema = z
  .object({ performanceId: z.uuid() })
  .meta({ id: 'ListPerformanceStaysQuery' });
export type ListPerformanceStaysQuery = z.infer<typeof listPerformanceStaysQuerySchema>;

const line = z.string().trim().min(1).max(MAX_STAY_TEXT);

/**
 * The booking reference is stored upper-cased (Atle, 2. september: «Nummer og referanse kan komme
 * i Caps lock»), for the reason `performance-travel.ts` gives at greater length: a code is printed
 * and read out in capitals, and `gnf-2027-114` beside `GNF-2027-114` reads as two bookings.
 */
const code = line.transform((value) => value.toUpperCase());

/**
 * The hotel's own page. `http(s)` only, checked here AND in the database
 * (`ck_performance_stays__website_url`).
 *
 * A link is rendered as an anchor, and `javascript:` in an href is the oldest hole there is.
 *
 * DET VAR TO KOPIER AV DEN REGELEN, og kommentaren over sa det høyt: «restated here because a stay
 * is not an advance section». Etter #16c er begge det samme kallet til `urlInputSchema`, og den er
 * stedet regelen bor. Aliaset blir stående så kallstedene ikke må skrives om.
 */
export const stayUrlSchema = urlInputSchema(2000);

/**
 * Check-out is never before check-in, and neither are a room's own nights.
 *
 * `>=`, not `>`: checking in and out on the same date is zero nights and happens on a one-day
 * festival where the act still needs somewhere to change — the database says the same in
 * `ck_performance_stays__nights_order`. Plain string comparison is safe here where it would not be
 * on a timestamp: `z.iso.date()` has exactly one spelling.
 */
const nightsInOrder = (from: string | null | undefined, to: string | null | undefined): boolean =>
  from == null || to == null || to >= from;

const STAY_ORDER = { message: 'Check-out cannot be before check-in.', path: ['checkOut'] };
const ROOM_ORDER = { message: 'The last night cannot be before the first.', path: ['nightsTo'] };

export const createPerformanceStayRequestSchema = z
  .object({
    /** The act these beds are for, or null for a booking that belongs to the year. */
    performanceId: z.uuid().nullish(),
    /** The year, named directly only when there is no act. See `oneOwner`. */
    editionId: z.uuid().nullish(),
    /** «Lydcrew», «fotograf NRK». Allowed on a row that does have an act too. */
    forWhom: forWhomSchema.nullish(),
    name: line,
    address: line.nullish(),
    contactName: line.nullish(),
    contactPhone: line.nullish(),
    bookingReference: code.nullish(),
    websiteUrl: stayUrlSchema.nullish(),
    checkIn: z.iso.date().nullish(),
    checkOut: z.iso.date().nullish(),
    checkInTime: clockTimeSchema.nullish(),
    checkOutTime: clockTimeSchema.nullish(),
    notes: z.string().trim().min(1).max(2000).nullish(),
  })
  .refine((body) => nightsInOrder(body.checkIn, body.checkOut), STAY_ORDER)
  .refine(oneOwner, ONE_OWNER)
  .meta({ id: 'CreatePerformanceStayRequest' });
export type CreatePerformanceStayRequest = z.infer<typeof createPerformanceStayRequestSchema>;

/**
 * `performanceId` never moves: a stay belongs to the act whose people sleep in it.
 *
 * The date refinement here only sees the PATCH. A request carrying `checkOut` alone is in order as
 * far as this schema can tell, and the service is what merges it with the stored `check_in` before
 * writing — see `PerformanceStayService`.
 */
export const updatePerformanceStayRequestSchema = z
  .object({
    name: line.optional(),
    address: line.nullish(),
    contactName: line.nullish(),
    contactPhone: line.nullish(),
    bookingReference: code.nullish(),
    websiteUrl: stayUrlSchema.nullish(),
    checkIn: z.iso.date().nullish(),
    checkOut: z.iso.date().nullish(),
    checkInTime: clockTimeSchema.nullish(),
    checkOutTime: clockTimeSchema.nullish(),
    notes: z.string().trim().min(1).max(2000).nullish(),
    /** «For hvem». Editable, unlike the owner above it — a label is not an identity. */
    forWhom: forWhomSchema.nullish(),
  })
  .refine((body) => nightsInOrder(body.checkIn, body.checkOut), STAY_ORDER)
  .meta({ id: 'UpdatePerformanceStayRequest' });
export type UpdatePerformanceStayRequest = z.infer<typeof updatePerformanceStayRequestSchema>;

export const createStayRoomRequestSchema = z
  .object({
    stayId: z.uuid(),
    roomNumber: z.string().trim().min(1).max(40).nullish(),
    roomType: z.string().trim().min(1).max(60).nullish(),
    nightsFrom: z.iso.date().nullish(),
    nightsTo: z.iso.date().nullish(),
    /** Optional, so a room can be booked before anybody decides who is in it. */
    occupantIds: z.array(z.uuid()).max(8).optional(),
  })
  .refine((body) => nightsInOrder(body.nightsFrom, body.nightsTo), ROOM_ORDER)
  .meta({ id: 'CreateStayRoomRequest' });
export type CreateStayRoomRequest = z.infer<typeof createStayRoomRequestSchema>;

export const updateStayRoomRequestSchema = z
  .object({
    /** Null clears it; absent leaves it. */
    roomNumber: z.string().trim().min(1).max(40).nullish(),
    roomType: z.string().trim().min(1).max(60).nullish(),
    nightsFrom: z.iso.date().nullish(),
    nightsTo: z.iso.date().nullish(),
  })
  .refine((body) => nightsInOrder(body.nightsFrom, body.nightsTo), ROOM_ORDER)
  .meta({ id: 'UpdateStayRoomRequest' });
export type UpdateStayRoomRequest = z.infer<typeof updateStayRoomRequestSchema>;

/**
 * REPLACE THE WHOLE ROOM, like a passenger list and for the same reason.
 *
 * Eight is the ceiling because a hotel room is a hotel room; a festival putting nine people in one
 * is describing a floor, which is several rows.
 */
export const setRoomOccupantsRequestSchema = z
  .object({ occupantIds: z.array(z.uuid()).max(8) })
  .meta({ id: 'SetRoomOccupantsRequest' });
export type SetRoomOccupantsRequest = z.infer<typeof setRoomOccupantsRequestSchema>;

/**
 * HOTELLKONTAKTEN SOM VISES (76): ressursens ansvarlige når et rom står på en ressurs med en, ellers
 * friteksten. Én regel for panelet, advancen, hotellista og dagsarket.
 */
export function stayContact(
  stay: Pick<PerformanceStayDto, 'contactName' | 'contactPhone' | 'placeContact'>,
): { name: string | null; phone: string | null; resourceName: string | null } {
  const place = stay.placeContact ?? null;
  if (place !== null)
    return { name: place.name, phone: place.phone, resourceName: place.resourceName };
  return { name: stay.contactName, phone: stay.contactPhone, resourceName: null };
}
